import clsx from "clsx";
import process from "process";
import React, { CSSProperties, useEffect, useId, useRef, useState } from "react";
import MEDIA from "../media";

export type Sizes = "xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "xxl" | "2xl" | "3xl" | "original";
export type MediaKey = keyof typeof MEDIA;

export const Img = <S extends Sizes>({
    noPreload,
    initialSize = "xs" as S,
    sizeMobile, // deprecated
    size,
    src: _src,
    style,
    className,
    customAspectRatio,
    twCover,
    ...props
}: {
    noPreload?: boolean;
    className?: string;
    alt: string;
    src: MediaKey;
    initialSize?: S;
    sizeMobile?: S;
    size: S;
    customAspectRatio?: boolean;
    twCover?: string;
} & React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const id = useId();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const ref = useRef<HTMLImageElement>(null);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
        if (noPreload) return;
        if (initialSize === size) return;
        if (!ref.current) return;

        const get = async () => {
            if (!ref.current) return;
            // const res = await fetch(getMedia(_src, "3xl"), { mode: "no-cors" });
            const res = await fetch(
                getMedia(_src, size),
                // "https://images.unsplash.com/photo-1726293993561-ae9901d8c6d0?q=80&w=3600&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
            );
            const imageBlob = await res.blob();
            const imageObjectURL = URL.createObjectURL(imageBlob);
            // console.log("ref.current", ref.current);
            // document.getElementById(id)!.src = imageUrl;
            ref.current.src = imageObjectURL;
        };

        var observer = new IntersectionObserver((entries, observer) => get(), {
            root: document.documentElement,
        });

        observer.observe(ref.current);

        return () => {
            observer.disconnect();
        };
    }, [_src, id, initialSize, size, noPreload]);

    return (
        <img
            ref={ref}
            id={id}
            style={{
                ...style,
                aspectRatio: customAspectRatio ? undefined : getDimensions(_src)?.aspectRatio,
            }}
            className={clsx(className, twCover)}
            decoding="async"
            src={getMedia(_src, noPreload ? size : initialSize)}
            {...props}
            alt={props.alt ?? ""}
        />
    );
};

export const useScreenDimensions = (breakPtWidth: number) => {
    let [width, _setWidth] = useState<"small" | "large">();
    useEffect(() => {
        _setWidth(window.screen.width < breakPtWidth ? "small" : "large");
    }, [breakPtWidth]);
    return width;
};

export const Svg = ({
    src: _src,
    size = "lg",
    ...props
}: {
    className?: string;
    alt: string;
    src: MediaKey;
    size?: Sizes;
    style?: CSSProperties;
}) => {
    // return <div />;
    return (
        // <img
        //     alt=""
        //     // type="image/svg+xml"
        //     style={props.style}
        //     className={props.className}
        //     src={getMedia(_src, "original")}
        //  />
        <object
            type="image/svg+xml"
            style={props.style}
            className={props.className}
            // alt={props.alt}
            aria-label={props.alt}
            data={getMedia(_src, size)}
        />
    );
};

export const SIZES = {
    original: 1500,
    //
    xxs: 200,
    xs: 400,
    sm: 800,
    md: 1100,
    lg: 1500,
    xl: 2500,
    xxl: 3750, // roughly 4k width
    "2xl": 5500,
    "3xl": 7500,
} satisfies Record<Sizes, number>;

export const getMedia = (_src: MediaKey, size = "original" as Sizes) => {
    let src = MEDIA[_src];
    if (!src) return ""; // TODO LOG this?

    const branch = process.env.GATSBY_BRANCH;
    const isImage = [".jpg", ".jpeg", ".png", ".webp", ".svg"].some((ending) => _src && _src.endsWith(ending));

    const isNonLocal = process.env.NODE_ENV === "production";
    if (isImage) {
        const width = SIZES[size] ?? SIZES.original;

        if (isNonLocal) {
            return `https://imagedelivery.net/UduvvRFAUoJF6YnYqkj5sw/${branch}${src.path}/w=${width}`;
        } else {
            return `http://localhost:8001/resize${src.path}?w=${width}`;
        }
    } else {
        if (isNonLocal) {
            return `https://homepage-assets.reonic.com/${branch}${src.path}`;
        } else {
            return `http://localhost:8001/static${src.path}`;
        }
    }
};

export const getDimensions = (_src: MediaKey) => {
    let src = MEDIA[_src];
    if (!src) return;
    if (!("dimensions" in src)) return;
    const dimensions = src.dimensions;
    return { ...dimensions, aspectRatio: dimensions.width / dimensions.height };
};
